import type {
  StationV1,
  StationV2,
  UrlV1,
  UrlV2,
} from "../models/station.js";
import { normalizeStreamType } from "../util/normalizeType.js";

/**
 * V1 parser — mirrors what `legacy/frontend/<country>/app/models/{radio,tv}.model.ts` do today.
 *
 * Intentionally tolerant of missing fields (`??` defaults) so a partially-
 * populated document from prod never throws — the legacy apps already
 * handle that quietly. Any change here is a change to the frozen contract;
 * touch with care.
 */
export function parseStationV1(raw: unknown): StationV1 {
  const r = (raw ?? {}) as Record<string, unknown>;
  const rawUrls = Array.isArray(r["urls"]) ? (r["urls"] as unknown[]) : [];
  return {
    live: Boolean(r["live"] ?? false),
    logo: typeof r["logo"] === "string" ? r["logo"] : "",
    name: typeof r["name"] === "string" ? r["name"] : "",
    urls: rawUrls.map(parseUrlV1),
  };
}

function parseUrlV1(raw: unknown): UrlV1 {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    // V1 casing is preserved in the model layer (`normalizeStreamType`
    // handles read-side normalization for V2 callers); default to "WEB"
    // to match the legacy TVModel behavior.
    type: normalizeStreamType(r["type"] ?? "WEB"),
    value: typeof r["value"] === "string" ? r["value"] : "",
  };
}

/**
 * V2 parser — calls into V1 for every field V1 already covers, then layers
 * on the optional additions. If parseStationV1 handles a field, parseStationV2
 * never touches it. That's what keeps a V2 bug from being able to break V1.
 */
export function parseStationV2(raw: unknown): StationV2 {
  const r = (raw ?? {}) as Record<string, unknown>;
  const base = parseStationV1(raw);
  const rawUrls = Array.isArray(r["urls"]) ? (r["urls"] as unknown[]) : [];

  const out: StationV2 = {
    ...base,
    urls: rawUrls.map((u, idx) => parseUrlV2(u, base.urls[idx])),
  };

  if (typeof r["id"] === "string") out.id = r["id"];
  if (typeof r["category"] === "string") out.category = r["category"];
  if (Array.isArray(r["tags"])) {
    out.tags = (r["tags"] as unknown[])
      .filter((t): t is string => typeof t === "string");
  }
  if (typeof r["description"] === "string") out.description = r["description"];
  if (typeof r["country"] === "string") out.country = r["country"].toLowerCase();
  if (typeof r["preview"] === "string") out.preview = r["preview"];
  if (typeof r["region"] === "string" && r["region"].trim()) out.region = r["region"].trim();
  const geo = r["geo"] as Record<string, unknown> | undefined;
  if (geo && typeof geo["lat"] === "number" && typeof geo["lng"] === "number") {
    out.geo = { lat: geo["lat"], lng: geo["lng"] };
  }
  const health = r["health"] as Record<string, unknown> | undefined;
  if (health && typeof health["checkedAt"] === "string") {
    out.health = { checkedAt: health["checkedAt"] };
    if (typeof health["latencyMs"] === "number") out.health.latencyMs = health["latencyMs"];
    if (typeof health["okUrls"] === "number") out.health.okUrls = health["okUrls"];
    if (typeof health["totalUrls"] === "number") out.health.totalUrls = health["totalUrls"];
  }
  if (r["updatedAt"] !== undefined && r["updatedAt"] !== null) {
    out.updatedAt = r["updatedAt"] as StationV2["updatedAt"];
  }
  return out;
}

function parseUrlV2(raw: unknown, v1Fallback: UrlV1 | undefined): UrlV2 {
  const r = (raw ?? {}) as Record<string, unknown>;
  const base: UrlV1 = v1Fallback ?? parseUrlV1(raw);
  const out: UrlV2 = { ...base };

  if (r["headers"] && typeof r["headers"] === "object") {
    out.headers = r["headers"] as Record<string, string>;
  }
  if (typeof r["userAgent"] === "string") out.userAgent = r["userAgent"];
  if (r["drm"] === "widevine" || r["drm"] === "fairplay") out.drm = r["drm"];
  if (typeof r["referrer"] === "string") out.referrer = r["referrer"];
  if (r["quality"] === "sd" || r["quality"] === "hd" || r["quality"] === "4k") {
    out.quality = r["quality"];
  }
  if (typeof r["fallbackIndex"] === "number") out.fallbackIndex = r["fallbackIndex"];
  return out;
}
